import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { labOrderStatusActions, labOrderStatusTransitions } from './labOrderStatus';

// Row menu plus the status change and delete dialogs, shared by web and mobile.
export default function LabOrderActionsMenu({ screen }) {
  const {
    menuAnchorEl,
    menuSelectedLabOrder,
    closeMenu,
    editLabOrder,
    statusDialog,
    statusDate,
    setStatusDate,
    statusExpectedReturnDate,
    setStatusExpectedReturnDate,
    clickChangeStatus,
    closeStatusDialog,
    confirmChangeStatus,
    openDeleteModal,
    clickDelete,
    closeDeleteModal,
    confirmDelete,
  } = screen;

  const nextStatuses = menuSelectedLabOrder
    ? (labOrderStatusTransitions[menuSelectedLabOrder.status] ?? [])
    : [];
  const statusAction = statusDialog ? labOrderStatusActions[statusDialog] : null;
  const isMissingDate =
    (Boolean(statusAction?.dateLabel) && !statusDate) ||
    (Boolean(statusAction?.expectedReturnDateLabel) && !statusExpectedReturnDate);

  return (
    <>
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeMenu}>
        {nextStatuses.map((nextStatus) => (
          <MenuItem key={nextStatus} onClick={() => clickChangeStatus(nextStatus)}>
            {labOrderStatusActions[nextStatus].menuLabel}
          </MenuItem>
        ))}
        {nextStatuses.length > 0 && <Divider />}
        <MenuItem onClick={editLabOrder}>Editar</MenuItem>
        <MenuItem onClick={clickDelete} sx={{ color: 'error.main' }}>
          Excluir
        </MenuItem>
      </Menu>

      <Dialog open={Boolean(statusAction)} onClose={closeStatusDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{statusAction?.dialogTitle}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: statusAction?.dateLabel ? 2 : 0 }}>
            {menuSelectedLabOrder &&
              `${menuSelectedLabOrder.procedure} — ${menuSelectedLabOrder.patientName}. `}
            {statusAction?.description}
          </Typography>
          {statusAction?.dateLabel && (
            <DatePicker
              label={statusAction.dateLabel}
              value={statusDate}
              onChange={(date) => setStatusDate(date)}
              format="DD/MM/YYYY"
              slotProps={{ textField: { fullWidth: true } }}
            />
          )}
          {statusAction?.expectedReturnDateLabel && (
            <DatePicker
              label={statusAction.expectedReturnDateLabel}
              value={statusExpectedReturnDate}
              onChange={(date) => setStatusExpectedReturnDate(date)}
              format="DD/MM/YYYY"
              minDate={statusDate ?? undefined}
              slotProps={{ textField: { fullWidth: true, sx: { mt: 2 } } }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStatusDialog}>Voltar</Button>
          <Button
            variant="contained"
            color={statusDialog === 'CANCELED' ? 'error' : 'primary'}
            onClick={confirmChangeStatus}
            disabled={isMissingDate}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteModal} onClose={closeDeleteModal} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir envio</DialogTitle>
        <DialogContent dividers>
          <Typography>
            O registro deste envio ao laboratório será removido permanentemente. Se o trabalho
            apenas não vai mais ser feito, prefira cancelar o envio. Deseja continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteModal}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

LabOrderActionsMenu.propTypes = {
  screen: PropTypes.shape({
    menuAnchorEl: PropTypes.object,
    menuSelectedLabOrder: PropTypes.shape({
      status: PropTypes.string,
      procedure: PropTypes.string,
      patientName: PropTypes.string,
    }),
    closeMenu: PropTypes.func.isRequired,
    editLabOrder: PropTypes.func.isRequired,
    statusDialog: PropTypes.string,
    statusDate: PropTypes.object,
    setStatusDate: PropTypes.func.isRequired,
    statusExpectedReturnDate: PropTypes.object,
    setStatusExpectedReturnDate: PropTypes.func.isRequired,
    clickChangeStatus: PropTypes.func.isRequired,
    closeStatusDialog: PropTypes.func.isRequired,
    confirmChangeStatus: PropTypes.func.isRequired,
    openDeleteModal: PropTypes.bool.isRequired,
    clickDelete: PropTypes.func.isRequired,
    closeDeleteModal: PropTypes.func.isRequired,
    confirmDelete: PropTypes.func.isRequired,
  }).isRequired,
};
